import { z } from "zod";

// Marketplace Listing Self-Service. Design:
// docs/superpowers/specs/2026-07-23-marketplace-listing-self-service-design.md
//
// Shared by the portal "My Listing" form (client-side validation), the worker
// (PUT /api/portal/listing/changes body + re-validation of the stored jsonb
// payload on approve), and the admin review UI (diff rendering).
//
// Column shapes verified against the live integrations-marketplace-BB project
// (ref hhisxvlzkivypaciecyi) on 2026-08-06: name/short_description/
// full_description are NOT NULL there, so they are required here — a nullable
// schema would let an approved request fail the marketplace write with a
// not-null violation. The four URL columns are nullable.

// http(s)-only URL. Bounds are structural; the "image URLs must point at
// Colab's public listing-media storage (or be unchanged from the live value)"
// rule CANNOT live in the schema — it depends on env.SUPABASE_URL and a live
// marketplace read — so it is enforced service-side via isAllowedImageUrl()
// in apps/api/src/services/listing-changes.ts.
const httpUrl = z
  .string()
  .trim()
  .max(500)
  .refine(
    (v) => {
      try {
        const protocol = new URL(v).protocol;
        return protocol === "http:" || protocol === "https:";
      } catch {
        return false;
      }
    },
    { message: "Must be a valid http(s) URL" },
  );

// No `.transform` anywhere in this schema: it is consumed by the SPA form and
// input/output divergence breaks react-hook-form's zodResolver (see the
// bucket-9 note in schemas/mdf.ts). The form maps "" -> null on submit.
export const listingChangeSchema = z.object({
  name: z.string().trim().min(2).max(80),
  short_description: z.string().trim().min(1).max(200),
  full_description: z.string().trim().min(1).max(5000),
  logo_url: httpUrl.nullable(),
  hero_image_url: httpUrl.nullable(),
  website_url: httpUrl.nullable(),
  documentation_url: httpUrl.nullable(),
});
export type ListingChangePayload = z.infer<typeof listingChangeSchema>;

// Admin decline body. Unlike MDF (reason required), the note is optional —
// the portal shows it verbatim when present.
export const listingDeclineSchema = z.object({
  note: z.string().trim().max(1000).optional(),
});
export type ListingDeclineInput = z.infer<typeof listingDeclineSchema>;

// The exact set of marketplace `integrations` columns a partner may edit.
// The worker builds its UPDATE key-by-key from this list — slug / status /
// is_featured and all child tables are Bookboost-controlled and can never
// leak into a write.
export const LISTING_FIELDS = [
  "name",
  "short_description",
  "full_description",
  "logo_url",
  "hero_image_url",
  "website_url",
  "documentation_url",
] as const;
export type ListingField = (typeof LISTING_FIELDS)[number];

// Which fields differ between the live listing and a proposed payload. Used
// by the admin diff view (row highlighting) and unit tests.
export function changedListingFields(
  live: ListingChangePayload,
  proposed: ListingChangePayload,
): ListingField[] {
  return LISTING_FIELDS.filter((field) => live[field] !== proposed[field]);
}

// Media upload limits. Structural bounds only; the worker enforces the exact
// MIME allowlist server-side (schemas stay transport-agnostic — mdf.ts
// pattern). Mirrors the listing-media bucket config in migration 0091.
export const LISTING_MEDIA_MIME_ALLOWLIST = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const;
export const LISTING_MEDIA_MAX_BYTES = 2_097_152; // 2 MB

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

// The live marketplace row as the worker reads it (the seven editable fields
// plus identity/display columns). `status` is free text on the marketplace
// side ("active" today) — Colab only displays it.
export type MarketplaceListingLiveDTO = ListingChangePayload & {
  id: number;
  slug: string;
  status: string | null;
  updated_at: string | null;
};

// Row for the admin link-picker.
export type MarketplaceListingOptionDTO = {
  id: number;
  name: string;
  slug: string;
  status: string | null;
};

export type ListingChangeStatus = "pending" | "approved" | "declined";

export type ListingChangeRequestDTO = {
  id: string;
  partner_id: string;
  integration_id: number;
  payload: ListingChangePayload;
  status: ListingChangeStatus;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
};

// Admin queue row: request + display context. `stale` = the linked
// marketplace row no longer exists (approve is blocked).
export type AdminListingChangeDTO = ListingChangeRequestDTO & {
  partner_name: string;
  listing_name: string | null;
  stale: boolean;
};

export type AdminListingChangeListDTO = {
  // false when MARKETPLACE_* secrets are unset — the queue (Colab data) is
  // still listed, but listing names are unresolved and approvals are blocked.
  configured: boolean;
  requests: AdminListingChangeDTO[];
};

// Drawer detail: the request plus the live row it would overwrite.
export type AdminListingChangeDetailDTO = {
  request: AdminListingChangeDTO;
  live: MarketplaceListingLiveDTO | null;
};

// GET /api/portal/listing. Discriminates on configured/linked so the portal
// nav + page can self-hide without extra requests:
//   { configured: false }                       — MARKETPLACE_* secrets unset
//   { configured: true, linked: false }         — partner has no listing link
//   { configured: true, linked: true, ... }     — the full editing surface
export type PortalListingDTO =
  | { configured: false }
  | { configured: true; linked: false }
  | {
      configured: true;
      linked: true;
      integration_id: number;
      // null with live_error set when the marketplace read failed
      // ("unavailable") or the linked row is gone ("stale_link").
      live: MarketplaceListingLiveDTO | null;
      live_error: "unavailable" | "stale_link" | null;
      pending: ListingChangeRequestDTO | null;
      // Most recent declined request, shown as a banner until the partner
      // resubmits. null when the partner has never had a decline.
      last_declined: {
        review_note: string | null;
        reviewed_at: string | null;
      } | null;
    };
